import { BiFilterAlt, BiSortAlt2 } from 'react-icons/bi';
import { BsPersonCheck, BsChat } from 'react-icons/bs';
import {
  Reducer,
  ReducerState,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  useMemo,
} from 'react';
import { debounce } from 'lodash';
import {
  Alert,
  Button,
  Col,
  Dropdown,
  DropdownButton,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import { ThWorkerRow } from '@company/components/th';
import { isMobile } from 'react-device-detect';
import ITalentsData, { ITalentData } from '@company/types/talents.type';
import * as workerApi from '@company/services/worker/api';
import * as jobAssignmentApi from '@company/services/jobAssignment/api';
import IFilterData, { ISortOrder } from '@company/types/talent-filterdata.type';
import { MilesDistance } from '@components/elements/MilesDistance/MilesDistance';
import { ITag } from '@th-types/tag.type';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@constants/queryKeys';
import { IWorkerData } from '@th-types/worker.type';
import { HiOutlineShare } from 'react-icons/hi';
import { JobHiringType } from '@constants/jobHiringType';
import { JobAssignmentStatus } from '@th-types/job-assignment-status.type';
import {
  LoaderImageGrid,
  LoaderPersonCard,
} from '@components/elements/ComponentLoader';
import { useChatContext } from '@company/state/chatContext';
import useAlert from '@hooks/useAlert';
import { useJobContext } from '@company/state/jobContext';
import { FiChevronDown } from '@react-icons/all-files/fi/FiChevronDown';
import { useJobAssignmentStatus } from '@hooks/useJobAssignmentStatus';
import {
  DEFAULT_ADDRESS,
  DEFAULT_ADDRESS_LATITUDE,
  DEFAULT_ADDRESS_LONGITUDE,
  DEFAULT_MILES_RADIUS,
  InitialSearchState,
} from '@constants/jobInitialSearch';
import { FilterType } from '@constants/jobFilterType';
import { JobSearchMessages } from '@constants/noDataFoundMessages';
import { NoDataFound } from '@components/elements/NoDataFound';
import { useCompanyContext } from '@company/state/companyContext';
import { WorkerModal } from '@company/components/WorkerModal';
import IActionButton from '@th-types/action-button.type';
import getNextPage, { MAX_SEARCH_OFFSET } from '@utils/Pagination';
import MapsAutoComplete from '@components/elements/AutoCompleteAddress/MapsAutoComplete';
import useUpdateWorker from '@company/state/useUpdateWorker/useUpdateWorker';
import { ThLoading } from '@components/elements';
import { useUserContext } from '@state/userContext';
import { UserRole } from '@constants/userRoles';
import { disableSearchButton } from '@company/components/GeneralSearchTalent/GeneralSearchTalent';
import { useJobShiftContext } from '@company/state/jobShiftContext';
import SearchTalentInput from '@company/components/SearchTalentInput/SearchTalentInput';
import { useJobWorkerShiftContext } from '@company/state/jobWorkerShiftContext';
import ModalJoinPremium from '@company/components/Payment/ModalJoinPremium';
import HireModal from '@company/components/HireModal';
import { getWorkerTags } from '@utils/Person';
import AdvancedFilters, { isNameSearchTooShort } from './AdvancedFilters';
import AdvancedFiltersBadges from './AdvancedFiltersBadges';
import { PaywallSidePanel } from './PaywallSidePanel';

function SearchTalent(): React.JSX.Element {
  const DEBOUNCE_RANGE_TIME = 500;
  const DEBOUNCE_TEXT_TIME = 500;
  const { openChat } = useChatContext();
  const [filterType, setFilterType] = useState(FilterType.ADDRESS);
  const [placeholderSearch, setPlaceholderSearch] = useState(
    `Search by ${filterType.toLowerCase()}`
  );
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState(false);
  const [openHireModal, setOpenHireModal] = useState<JobHiringType | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);
  const [showWorkerProfile, setShowWorkerProfile] = useState(false);
  const [selectedWorkerProfile, setSelectedWorkerProfile] =
    useState<ITalentData>();
  const [workerProfileId, setWorkerProfileId] = useState<number | null>(null);
  const [workerProfileJobAssignmentId, setWorkerProfileJobAssignmentId] =
    useState<number | null>(null);
  const [workerProfileJobId, setWorkerProfileJobId] = useState<number | null>(
    null
  );

  const {
    shifts,
    refetch: refetchShifts,
    selectedWorker,
    setStatusFilter,
    setSelectedWorker,
    setFilteredShifts,
    setSelectedShifts,
  } = useJobShiftContext();

  const [isLoadingAction, setLoadingAction] = useState(false);
  const [remainingTalents, setRemainingTalents] = useState(0);
  const { job, initialSearch, setInitialSearch, isInternalMember } =
    useJobContext();
  const { premium: isPremiumCompany } = useCompanyContext();
  const { id, hasRole } = useUserContext();
  const nameOrEmailRef = useRef<HTMLInputElement>(null);

  const initialFilters = {
    jobId: job?.id,
    filterType: FilterType.ADDRESS,
    filterWorkers: 'ALL',
    miles: DEFAULT_MILES_RADIUS,
    address:
      job && !job.nationalJob ? job.address.googleAddress : DEFAULT_ADDRESS,
    addressLatitude:
      job && !job.nationalJob ? job.address.latitude : DEFAULT_ADDRESS_LATITUDE,
    addressLongitude:
      job && !job.nationalJob
        ? job.address.longitude
        : DEFAULT_ADDRESS_LONGITUDE,
    name: '',
    email: '',
    sortOrder: ISortOrder.DESC,
  };

  type IFilterReducerData = IFilterData & Reducer<any, any>; // To handle the crybaby TS compiler
  // Allow setting partial filters state
  const filtersReducer: Reducer<IFilterData, Partial<IFilterData>> = (
    state: IFilterData,
    newState: Partial<IFilterData>
  ): IFilterData => ({
    ...state,
    ...newState,
  });
  const [filters, setFilters] = useReducer(
    filtersReducer,
    initialFilters as ReducerState<IFilterReducerData>
  );

  const [advancedFilters, setAdvancedFilters] = useState({} as IFilterData);
  const [rawAddressInput, setRawAddressInput] = useState('');
  const [selectedPlace] = useState<google.maps.places.PlaceResult | null>(null);

  const { showSuccessAlert, showWarningAlert, alertMessage } = useAlert();
  const { updateWorker, updatedWorker, fetchUpdatedWorker } = useUpdateWorker();

  const { setIsJobWorkerSideModalOpen } = useJobWorkerShiftContext();
  const jobAssignmentStatus = useJobAssignmentStatus();

  const handleShowWorkerProfile = (talent: ITalentData) => {
    if (!isPremiumCompany) {
      return;
    }
    setSelectedWorkerProfile(talent);
    setWorkerProfileId(talent.worker.id);
    setWorkerProfileJobAssignmentId(talent.jobAssignment?.id || null);
    setWorkerProfileJobId(job?.id || null);
    setShowWorkerProfile(true);
    refetchShifts();
    setSelectedShifts([]);
    setIsJobWorkerSideModalOpen(true);
    setSelectedWorker(talent.worker);
  };

  const offerOrHire = (talent: ITalentData | undefined) => {
    const assignmentStatus = talent?.jobAssignment?.status;
    const { isApplicant, isEligibleForOffer } =
      jobAssignmentStatus(assignmentStatus);

    if (!isPremiumCompany) {
      return null;
    }
    if (assignmentStatus === undefined) {
      return JobHiringType.OFFER;
    }
    if (isApplicant()) {
      return JobHiringType.HIRE;
    }
    if (
      talent?.jobPlacement?.status !== JobAssignmentStatus.OFFERED &&
      isEligibleForOffer()
    ) {
      return JobHiringType.OFFER;
    }

    return null;
  };

  const handleShowOfferHireModal = (talent: ITalentData) => {
    if (!isPremiumCompany) {
      return;
    }
    setSelectedWorkerProfile(talent);
    setOpenHireModal(offerOrHire(talent));
    setSelectedWorker(talent.worker);
  };

  const handleSuccessHireModal = (message?: string) => {
    if (message) {
      showSuccessAlert(message);
    }
    updateWorker({
      workerId: selectedWorker?.id,
      jobId: job?.id,
    });
    setOpenHireModal(null);
    setSelectedWorker(undefined);
    setStatusFilter([]);
  };

  const handleFailureHireModal = (message?: string) => {
    if (message) {
      showWarningAlert(message);
    }
    setOpenHireModal(null);
    setSelectedWorker(undefined);
    setStatusFilter([]);
  };

  const handleCloseHireModal = () => {
    setOpenHireModal(null);
    setSelectedWorker(undefined);
    setStatusFilter([]);
  };

  const handleToggleAdvancedFilters = () => {
    if (!isPremiumCompany) {
      return;
    }
    setOpenAdvancedFilters(!openAdvancedFilters);
  };

  const handleCloseAdvancedFilters = () => {
    if (!isPremiumCompany) {
      return;
    }
    setOpenAdvancedFilters(false);
  };

  const fetchTalents = useCallback(
    async ({ pageParam = 0 }) => {
      if (
        isNameSearchTooShort(filters.filterType, filters.name) ||
        (!filters.name && !filters.address && !filters.email)
      ) {
        return new Promise<ITalentsData>((resolve) => {
          const emptyResponse: ITalentsData = {} as ITalentsData;
          resolve(emptyResponse);
        });
      }
      return workerApi.fetchTalents(
        { ...filters, ...advancedFilters },
        pageParam
      );
    },
    [filters, advancedFilters]
  );

  const fetchTalentsQueryKey = useMemo(
    () => [QueryKeys.COMPANY_TALENTS, job?.id],
    [job?.id]
  );
  const queryClient = useQueryClient();

  const {
    data,
    isSuccess,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey: fetchTalentsQueryKey,
    queryFn: fetchTalents,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    // TODO: enable this when component is on focus !!!
    enabled: job !== undefined && job?.id !== undefined,
    getNextPageParam: (lastPage, allPages) => getNextPage(lastPage, allPages),
    initialPageParam: 0,
  });

  useEffect(() => {
    if (fetchUpdatedWorker) {
      const fetchUpdatedWorkerPayload = {
        updatedWorker,
        data,
        queryClient,
        queryKey: fetchTalentsQueryKey,
      };
      fetchUpdatedWorker(fetchUpdatedWorkerPayload);
    }
  }, [
    fetchUpdatedWorker,
    updatedWorker,
    data,
    queryClient,
    fetchTalentsQueryKey,
  ]);

  const deferredFetch = useCallback(
    () => setTimeout(() => refetch(), 100),
    [refetch]
  ); // because useState is async

  const hasSomeDataToShow = !!data && data?.pages?.[0].results?.length > 0;

  const handleCloseWorkerProfile = () => {
    if (!isPremiumCompany) {
      return;
    }
    if (shifts) {
      setFilteredShifts(shifts);
    }
    setShowWorkerProfile(false);
    setWorkerProfileId(null);
    setWorkerProfileJobAssignmentId(null);
    setWorkerProfileJobId(null);
    setIsJobWorkerSideModalOpen(false);
    setStatusFilter([]);
    setSelectedWorker(undefined);
  };

  useEffect(() => {
    if (job?.id) {
      setFilters({
        jobId: job?.id,
        address: job && !job.nationalJob ? job.address.googleAddress : '',
        addressLatitude:
          job && !job.nationalJob ? job.address.latitude : undefined,
        addressLongitude:
          job && !job.nationalJob ? job.address.longitude : undefined,
      });

      deferredFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  useEffect(() => {
    if (data && data?.pages[0]?.results) {
      let totalResults = 0;
      data.pages.map((page) => {
        totalResults += page.results.length;
        return page;
      });
      const count = data?.pages[0]?.pagination?.count ?? 0;
      setRemainingTalents(count - totalResults);
    }
  }, [data]);

  useEffect(() => {
    if (nameOrEmailRef.current) {
      nameOrEmailRef.current.focus();
    }
  });

  useEffect(() => {
    if (filterType === FilterType.NAME || filterType === FilterType.EMAIL) {
      setFilters({
        ...initialFilters,
        filterType,
        address: undefined,
        addressLatitude: undefined,
        addressLongitude: undefined,
        miles: undefined,
      });
      if (nameOrEmailRef.current) {
        nameOrEmailRef.current.value = '';
      }
    } else {
      setFilters({ ...initialFilters, filterType });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const handleInputOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { value } = event.target;
    setFilters({
      name: filterType === FilterType.NAME ? value : undefined,
      email: filterType === FilterType.EMAIL ? value : undefined,
      address: filterType === FilterType.ADDRESS ? value : undefined,
      sortOrder: ISortOrder.DESC,
    });
  };

  useEffect(() => {
    if (isMobile) {
      refetch();
    } else if (
      filterType !== FilterType.ADDRESS &&
      !filters.name &&
      !filters.email
    ) {
      // refetch when the user clears the input
      refetch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.name, filters.email, filters.miles]);

  const handleInitialSearch = debounce(async () => {
    deferredFetch();
  }, DEBOUNCE_TEXT_TIME);

  useEffect(() => {
    if (initialSearch !== InitialSearchState.DONE) {
      setInitialSearch(InitialSearchState.DONE);
      handleInitialSearch();
    }
  }, [handleInitialSearch, initialSearch, setInitialSearch]);

  const handleMilesRange = debounce((newMilesRange: number | string) => {
    if (!isPremiumCompany) {
      return;
    }
    setFilters({
      miles: newMilesRange,
    });
  }, DEBOUNCE_RANGE_TIME);

  useEffect(() => {
    refetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.sortBy, filters.sortOrder]);

  const handleSortSearchTalents = async () => {
    if (!isPremiumCompany) {
      return;
    }
    setFilters({
      sortOrder:
        filters.sortOrder !== undefined && filters.sortOrder === ISortOrder.DESC
          ? ISortOrder.ASC
          : ISortOrder.DESC,
    });
  };

  const handleSetAdvancedFilters = (newFilters: IFilterData) => {
    if (!isPremiumCompany) {
      return;
    }
    setAdvancedFilters({ ...newFilters });
  };

  const shareJobToWorker = async (talent: ITalentData) => {
    if (!isPremiumCompany) {
      return;
    }
    setLoadingAction(true);
    const result = await jobAssignmentApi.shareJobToWorker(
      job?.id,
      talent.worker.id
    );
    setLoadingAction(false);
    if (result && result.success) {
      showSuccessAlert(result.message);
      updateWorker({
        workerId: talent.worker.id,
        jobId: job?.id,
      });
    } else {
      showWarningAlert(
        'An error has occurred while was trying to share job with worker'
      );
    }
  };

  const handleOpenChatClick = useCallback(
    (talent: ITalentData) => {
      if (!isPremiumCompany) {
        return;
      }
      const { shouldShowWorkerInfoInChat } = jobAssignmentStatus(undefined);

      openChat(
        talent.worker,
        talent.jobAssignment?.id ?? null,
        shouldShowWorkerInfoInChat({
          status: talent.jobAssignment?.status ?? '',
        })
      );
    },
    [isPremiumCompany, openChat, jobAssignmentStatus]
  );

  const showModalCompanyPremiumMessage = (
    success: boolean,
    message: string
  ) => {
    if (success) {
      showSuccessAlert(message);
    } else {
      showWarningAlert(message);
    }
  };

  const isHired = (talent: ITalentData) => {
    const status = talent?.jobAssignment?.status;
    return jobAssignmentStatus(status).isHired();
  };

  const getActions = (talent: ITalentData) => {
    if (!job?.datePublished) {
      return [];
    }
    const actions: IActionButton[] = [];

    if (job?.isActive || talent.jobAssignment?.hired) {
      actions.push({
        label: 'Message',
        variant: 'light',
        size: 'sm',
        action: () => handleOpenChatClick(talent),
        icon: <BsChat size={10} />,
      });
    }

    if (job?.isActive) {
      if (!isHired(talent) && offerOrHire(talent)) {
        actions.push({
          label: offerOrHire(talent) || '',
          variant: 'light',
          size: 'sm',
          action: () => handleShowOfferHireModal(talent),
          icon: <BsPersonCheck size={10} />,
        });
      }

      // Show SHARE button only if:
      // 1. No jobRecommendation exists (job not already shared via recommendation)
      // 2. AND either no assignment/placement OR assignment is for a different job
      if (
        !talent.jobRecommendation &&
        ((!talent.jobAssignment && !talent.jobPlacement) ||
          (talent.jobAssignment && talent.jobAssignment.job.id !== job.id))
      ) {
        actions.push({
          label: 'SHARE',
          variant: 'light',
          size: 'sm',
          action: () => shareJobToWorker(talent),
          icon: <HiOutlineShare size={10} />,
        });
      }
    }
    return actions;
  };

  const getTags = (matchSkills: boolean, worker: IWorkerData): ITag[] => {
    return getWorkerTags(worker, matchSkills);
  };

  const handleFilterType = (type: any) => {
    setFilterType(type);
    setFilters({
      filterType: type,
    });
    setPlaceholderSearch(
      `Search by ${type.toLowerCase().replace('email', 'e-mail')}`
    );
    if (
      type === FilterType.ADDRESS &&
      filters.address === undefined &&
      job?.id
    ) {
      setFilters({
        jobId: job?.id,
        address: job && !job.nationalJob ? job.address.googleAddress : '',
        addressLatitude:
          job && !job.nationalJob ? job.address.latitude : undefined,
        addressLongitude:
          job && !job.nationalJob ? job.address.longitude : undefined,
      });
    }
  };

  const renderWorkers = () => {
    if (isMobile && openAdvancedFilters) {
      return '';
    }

    if (isFetching && !isFetchingNextPage) {
      return (
        <Col md={12}>
          {isMobile ? (
            <LoaderPersonCard />
          ) : (
            <LoaderImageGrid column={openAdvancedFilters ? 4 : 6} />
          )}
        </Col>
      );
    }

    const renderNoDataFound = () => (
      <Row>
        <Col>
          <NoDataFound
            noResultsMessage={JobSearchMessages.NO_RESULTS}
            helperMessage={JobSearchMessages.NO_RESULTS_UPDATE_FILTERS}
          />
        </Col>
      </Row>
    );

    const renderDataFound = () => {
      return (
        <Row>
          {data?.pages?.map((page) =>
            page?.results?.map((talent, index) => (
              <Col
                key={talent.worker.id}
                md={openAdvancedFilters ? 3 : 2}
                className="mt-3"
              >
                <div className="panel">
                  <ThWorkerRow
                    index={index + 1}
                    worker={talent.worker}
                    showVideoIntro
                    distance={talent.distance}
                    isFavoritedByCompany={talent.isFavoritedByCompany}
                    isBlockedByCompany={talent.isBlockedByCompany}
                    thumbStyle
                    tags={getTags(talent.matchSkills, talent.worker)}
                    actionButtons={getActions(talent)}
                    openDetails={() => handleShowWorkerProfile(talent)}
                    isHired={isHired(talent)}
                    isPremiumCompany={isPremiumCompany}
                    isInternalMember={isInternalMember(id)}
                    isCompanyAdmin={hasRole(UserRole.ROLE_COMPANY_ADMIN)}
                  />
                </div>
              </Col>
            ))
          )}
          {selectedWorkerProfile && openHireModal && (
            <HireModal
              jobAssignment={selectedWorkerProfile.jobAssignment}
              worker={selectedWorkerProfile.worker}
              handleSuccess={handleSuccessHireModal}
              handleFailure={handleFailureHireModal}
              handleClose={handleCloseHireModal}
              hiringType={openHireModal}
            />
          )}
          {!isPremiumCompany && (
            <PaywallSidePanel setShowModal={setShowModal} />
          )}
        </Row>
      );
    };

    return (
      <>
        {isSuccess &&
          (hasSomeDataToShow ? renderDataFound() : renderNoDataFound())}
        {isFetchingNextPage && (
          <Col md={12}>
            {isMobile ? (
              <LoaderPersonCard />
            ) : (
              <LoaderImageGrid column={openAdvancedFilters ? 4 : 6} />
            )}
          </Col>
        )}
        {hasNextPage && isPremiumCompany && (
          <Row>
            <Col className="text-center mt-3">
              <Button
                variant="light"
                onClick={() => fetchNextPage()}
                disabled={
                  isFetching ||
                  (data?.pages?.length ?? 0) * 24 >= MAX_SEARCH_OFFSET
                }
              >
                <b>SHOW MORE ({remainingTalents})</b>
              </Button>
            </Col>
          </Row>
        )}
      </>
    );
  };

  const isAddressInputDirty =
    filterType === FilterType.ADDRESS &&
    rawAddressInput !== (selectedPlace?.formatted_address || '');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && isPremiumCompany && !isFetching) {
      refetch();
    }
  };

  return (
    <div>
      {(isLoadingAction || updatedWorker.isLoadingUpdateWorker) && (
        <ThLoading />
      )}
      <Row>
        <Col lg={2} xs={4}>
          <DropdownButton
            variant="light w-100"
            disabled={isFetching || !isPremiumCompany}
            title={
              <b style={{ fontSize: isMobile ? '14px' : '' }}>
                {(isMobile ? '' : 'FILTER BY ') + filterType}{' '}
                <FiChevronDown size={24} />
              </b>
            }
            onSelect={(e) => handleFilterType(e)}
          >
            <Dropdown.Item eventKey={FilterType.NAME}>
              {FilterType.NAME}
            </Dropdown.Item>
            <Dropdown.Item eventKey={FilterType.ADDRESS}>
              {FilterType.ADDRESS}
            </Dropdown.Item>
            <Dropdown.Item eventKey={FilterType.EMAIL}>
              {FilterType.EMAIL}
            </Dropdown.Item>
          </DropdownButton>
        </Col>

        <Col md={isMobile ? 5 : 4} xs={8}>
          {filterType === FilterType.ADDRESS ? (
            <MapsAutoComplete
              defaultAddress={filters.address || DEFAULT_ADDRESS}
              setInputValue={setRawAddressInput}
              disabled={!job?.nationalJob} // Always use the job address, except for national jobs
            />
          ) : (
            <SearchTalentInput
              placeholder={placeholderSearch}
              disabled={isFetching || !isPremiumCompany}
              onChange={handleInputOnChange}
              name={filterType.toLowerCase()}
              inputRef={nameOrEmailRef}
              onKeyDown={handleKeyDown}
            />
          )}
        </Col>

        {filterType === FilterType.ADDRESS && (
          <Col md={2} xs={6} className={isMobile ? 'mt-3' : ''}>
            <MilesDistance
              handleMilesRange={handleMilesRange}
              disabled={isFetching || !isPremiumCompany}
              milesRange={filters.miles ?? DEFAULT_MILES_RADIUS}
            />
          </Col>
        )}

        {!isMobile && (
          <Col md={1}>
            {isAddressInputDirty ? (
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip>
                    Please select an address from the autocomplete dropdown
                  </Tooltip>
                }
              >
                <span style={{ display: 'inline-block' }}>
                  <Button
                    disabled={
                      disableSearchButton(
                        isPremiumCompany,
                        isFetching,
                        filters
                      ) || isAddressInputDirty
                    }
                    type="submit"
                    onClick={() => refetch()}
                  >
                    SEARCH
                  </Button>
                </span>
              </OverlayTrigger>
            ) : (
              <Button
                disabled={
                  disableSearchButton(isPremiumCompany, isFetching, filters) ||
                  isAddressInputDirty
                }
                type="submit"
                onClick={() => refetch()}
              >
                SEARCH
              </Button>
            )}
          </Col>
        )}

        {isPremiumCompany && (
          <Col
            md={3}
            xs={6}
            className={`${
              isMobile ? 'mt-3 d-inline-flex justify-content-end' : ''
            } text-end`}
            style={{ marginLeft: 'auto' }}
          >
            <Button
              variant="light"
              disabled={data?.pages[0]?.results?.length === 0 || isFetching}
              onClick={handleSortSearchTalents}
            >
              <BiSortAlt2 size={25} />
            </Button>
            <Button
              className="ms-3"
              variant={openAdvancedFilters ? 'primary' : 'light'}
              disabled={isFetching}
              onClick={handleToggleAdvancedFilters}
            >
              <BiFilterAlt size={25} />
              {!isMobile && <b className="">&nbsp;ADVANCED FILTERS</b>}
            </Button>
          </Col>
        )}
      </Row>
      <Row>
        <Col md={openAdvancedFilters ? 8 : 12}>
          <AdvancedFiltersBadges
            handleClose={handleCloseAdvancedFilters}
            advancedFilters={advancedFilters}
            setAdvancedFilters={handleSetAdvancedFilters}
          />
          {renderWorkers()}
        </Col>
        {openAdvancedFilters && (
          <Col md={4}>
            <AdvancedFilters
              filterByStatus
              handleClose={handleCloseAdvancedFilters}
              advancedFilters={advancedFilters}
              setAdvancedFilters={setAdvancedFilters}
              onSearch={() => refetch()}
            />
          </Col>
        )}
      </Row>
      <WorkerModal
        workerId={workerProfileId || undefined}
        jobAssignmentId={workerProfileJobAssignmentId || undefined}
        jobId={workerProfileJobId || undefined}
        show={showWorkerProfile && !!workerProfileId}
        onClose={handleCloseWorkerProfile}
        showJobDetails
      />
      {showModal && (
        <ModalJoinPremium
          showMessage={showModalCompanyPremiumMessage}
          setShowModal={setShowModal}
        />
      )}
      <Alert
        show={alertMessage.show}
        variant={alertMessage.variant}
        className="alert-fixed"
        style={{ width: '20rem' }}
      >
        <Alert.Heading>{alertMessage.message}</Alert.Heading>
      </Alert>
    </div>
  );
}

export default SearchTalent;
